import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  MapPin,
  Phone,
  ArrowLeft,
  BedDouble,
  Users,
  Wifi,
  Tv,
  Droplets,
  Check,
  IndianRupee,
  Clock,
  ChevronDown,
  ChevronUp
} from 'lucide-react';
import PublicLayout from '../../components/layout/PublicLayout';
import SEO from '../../components/SEO';
import { stationsAPI } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const TIME_SLOTS = ['1hr', '3hr', '6hr', '9hr', '12hr', '24hr'];
const SLOT_LABELS = {
  '1hr': '1 Hour',
  '3hr': '3 Hours',
  '6hr': '6 Hours',
  '9hr': '9 Hours',
  '12hr': '12 Hours',
  '24hr': '24 Hours'
};

const StationRooms = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [station, setStation] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showTariffTable, setShowTariffTable] = useState(true);
  const [expandedCategory, setExpandedCategory] = useState(null);

  useEffect(() => {
    fetchStationDetails();
  }, [id]);

  const fetchStationDetails = async () => {
    try {
      const response = await stationsAPI.getOne(id);
      setStation(response.data.station);
      setCategories(response.data.categories);
    } catch (error) {
      console.error('Error fetching station:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBookNow = () => {
    navigate(`/book/${id}`, { state: { selectedCategory } });
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      </PublicLayout>
    );
  }

  if (!station) {
    return (
      <PublicLayout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center">
          <h2 className="text-2xl font-display font-bold text-midnight-900">Station not found</h2>
          <Link to="/" className="mt-4 btn-primary">Go Back Home</Link>
        </div>
      </PublicLayout>
    );
  }

  const getAmenityIcon = (amenity) => {
    const lower = amenity.toLowerCase();
    if (lower.includes('wifi')) return Wifi;
    if (lower.includes('tv')) return Tv;
    if (lower.includes('water') || lower.includes('hot')) return Droplets;
    return Check;
  };

  return (
    <PublicLayout>
      <SEO
        title={`${station.name} Station — Retiring Rooms & Accommodation`}
        description={`Book comfortable retiring rooms at ${station.name} Railway Station in ${station.city}, ${station.state}. AC, Non-AC, Dormitory, and Deluxe rooms with flexible time slots.`}
        canonical={`/station/${station._id}`}
        keywords={`${station.name} retiring room, ${station.city} station accommodation, ${station.code} railway station rooms, book room ${station.name}`}
      />
      {/* Station Header */}
      <section className="bg-gradient-to-br from-midnight-950 via-midnight-900 to-railway-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Stations</span>
          </Link>

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-3 mb-4">
                <span className="px-4 py-2 bg-railway-500 rounded-lg font-bold text-lg">
                  {station.code}
                </span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-display font-bold">
                {station.name}
              </h1>
              <div className="flex flex-wrap items-center gap-4 mt-4 text-white/70">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  <span>{station.city}, {station.state}</span>
                </div>
                {station.contact && (
                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    <span>{station.contact}</span>
                  </div>
                )}
              </div>
              {station.adminContact && (
                <div className="mt-3 inline-flex items-center gap-2 bg-railway-500/20 backdrop-blur-sm border border-railway-500/30 px-4 py-2 rounded-lg">
                  <Phone className="w-4 h-4 text-railway-300" />
                  <span className="text-railway-200 text-sm">For booking assistance: </span>
                  <a href={`tel:+91${station.adminContact}`} className="text-white font-medium hover:text-railway-300 transition-colors">
                    +91 {station.adminContact}
                  </a>
                </div>
              )}
            </div>

            <button
              onClick={handleBookNow}
              className="btn-primary text-lg px-8"
            >
              Book a Room
            </button>
          </div>
        </div>
      </section>

      {/* Categories Grid */}
      <section className="py-16 bg-gradient-to-b from-midnight-100 to-midnight-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold text-midnight-900">
              Available Room Categories
            </h2>
            <p className="mt-3 text-midnight-600">
              Select a category to view details and pricing
            </p>
          </div>

          {categories.length === 0 ? (
            <div className="text-center py-12">
              <BedDouble className="w-16 h-16 text-midnight-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-midnight-700">No rooms available</h3>
              <p className="text-midnight-500 mt-2">Check back later for availability</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category) => (
                <div
                  key={category._id}
                  className={`
                    card cursor-pointer transition-all duration-300
                    ${selectedCategory?._id === category._id
                      ? 'ring-4 ring-railway-500 shadow-2xl scale-[1.02]'
                      : 'hover:shadow-xl'
                    }
                  `}
                >
                  <div
                    className="p-6"
                    onClick={() => setSelectedCategory(
                      selectedCategory?._id === category._id ? null : category
                    )}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <h3 className="text-xl font-display font-semibold text-midnight-900">
                        {category.name}
                      </h3>
                      <div className={`
                        w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors
                        ${selectedCategory?._id === category._id
                          ? 'bg-railway-500 border-railway-500'
                          : 'border-midnight-300'
                        }
                      `}>
                        {selectedCategory?._id === category._id && (
                          <Check className="w-4 h-4 text-white" />
                        )}
                      </div>
                    </div>

                    <p className="text-midnight-600 text-sm mb-4">
                      {category.description || 'Comfortable accommodation for travelers'}
                    </p>

                    {/* Amenities */}
                    {category.amenities && category.amenities.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {category.amenities.map((amenity, index) => {
                          const Icon = getAmenityIcon(amenity);
                          return (
                            <span
                              key={index}
                              className="inline-flex items-center gap-1 px-2 py-1 bg-midnight-100 rounded-md text-xs text-midnight-600"
                            >
                              <Icon className="w-3 h-3" />
                              {amenity}
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {/* Stats */}
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1 text-green-600">
                        <BedDouble className="w-4 h-4" />
                        <span>{category.availableRooms} available</span>
                      </div>
                      <div className="flex items-center gap-1 text-midnight-500">
                        <Users className="w-4 h-4" />
                        <span>{category.totalRooms} total</span>
                      </div>
                    </div>
                  </div>

                  {/* Expandable Pricing */}
                  <div className="border-t border-midnight-100">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedCategory(
                          expandedCategory === category._id ? null : category._id
                        );
                      }}
                      className="w-full px-6 py-3 flex items-center justify-between bg-midnight-50 hover:bg-midnight-100 transition-colors"
                    >
                      <span className="text-sm font-medium text-midnight-700">View Pricing</span>
                      {expandedCategory === category._id ? (
                        <ChevronUp className="w-4 h-4 text-midnight-500" />
                      ) : (
                        <ChevronDown className="w-4 h-4 text-midnight-500" />
                      )}
                    </button>

                    {expandedCategory === category._id && (
                      <div className="px-6 py-4 bg-white border-t border-midnight-100">
                        <div className="grid grid-cols-6 gap-2">
                          {TIME_SLOTS.map(slot => (
                            <div key={slot} className="text-center">
                              <div className="text-xs text-midnight-500 mb-1">{slot}</div>
                              <div className="flex items-center justify-center gap-0.5 font-bold text-midnight-900">
                                <IndianRupee className="w-3 h-3" />
                                <span className="text-sm">{category.pricing?.[slot] || '—'}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Price Footer */}
                  <div className="px-6 py-4 bg-gradient-to-r from-midnight-900 to-midnight-800 text-white">
                    <div className="flex items-center justify-between">
                      <span className="text-white/70 text-sm">Starting from</span>
                      <div className="flex items-center gap-1 text-2xl font-bold">
                        <IndianRupee className="w-5 h-5" />
                        <span>{category.pricing?.['1hr'] || category.basePrice}</span>
                        <span className="text-sm font-normal text-white/60">/hr</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Book Now Button */}
          {categories.length > 0 && (
            <div className="mt-12 text-center">
              <button
                onClick={handleBookNow}
                className="btn-primary text-lg px-12 py-4"
              >
                {selectedCategory
                  ? `Book ${selectedCategory.name} Room`
                  : 'Request a Room'
                }
              </button>
              {/* <p className="mt-3 text-midnight-500 text-sm">
                No login required. Admin will allocate a room based on availability.
              </p> */}
            </div>
          )}
        </div>
      </section>

      {/* Tariff Table */}
      <section className="py-8 bg-gradient-to-b from-midnight-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => setShowTariffTable(!showTariffTable)}
            className="w-full flex items-center justify-between p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-railway-500/10 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-railway-600" />
              </div>
              <div className="text-left">
                <h3 className="font-display font-semibold text-midnight-900">Room Tariff Chart</h3>
                <p className="text-sm text-midnight-500">View pricing for all time slots</p>
              </div>
            </div>
            {showTariffTable ? (
              <ChevronUp className="w-5 h-5 text-midnight-400" />
            ) : (
              <ChevronDown className="w-5 h-5 text-midnight-400" />
            )}
          </button>

          {showTariffTable && (
            <div className="mt-4 bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-midnight-900 to-midnight-800 text-white">
                    <tr>
                      <th className="px-6 py-4 text-left font-display font-semibold">Room Category</th>
                      {TIME_SLOTS.map(slot => (
                        <th key={slot} className="px-4 py-4 text-center font-display font-semibold min-w-[90px]">
                          {SLOT_LABELS[slot]}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-midnight-100">
                    {categories.map((category, index) => (
                      <tr
                        key={category._id}
                        className={`
                          transition-colors cursor-pointer
                          ${selectedCategory?._id === category._id
                            ? 'bg-railway-50'
                            : index % 2 === 0 ? 'bg-white' : 'bg-midnight-50/50'
                          }
                          hover:bg-railway-50/50
                        `}
                        onClick={() => setSelectedCategory(
                          selectedCategory?._id === category._id ? null : category
                        )}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            {selectedCategory?._id === category._id && (
                              <div className="w-2 h-2 bg-railway-500 rounded-full"></div>
                            )}
                            <div>
                              <div className="font-medium text-midnight-900">{category.name}</div>
                              <div className="text-xs text-green-600">
                                {category.availableRooms} rooms available
                              </div>
                            </div>
                          </div>
                        </td>
                        {TIME_SLOTS.map(slot => (
                          <td key={slot} className="px-4 py-4 text-center">
                            <div className="inline-flex items-center gap-0.5 font-semibold text-midnight-900">
                              <IndianRupee className="w-3 h-3" />
                              <span>{category.pricing?.[slot] || '—'}</span>
                            </div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-3 bg-midnight-50 border-t border-midnight-100">
                <p className="text-xs text-midnight-500 flex items-center gap-2">
                  <Clock className="w-3 h-3" />
                  Prices are per room/bed. Click on a row to select the category.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
};

export default StationRooms;
